import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Typography
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const PerfilSchema = Yup.object().shape({
  perf_name: Yup.string().required(() => i18n.t("profiles.validation.required")),
});

// Mesma ordem dos itens da sidebar (layout/MainListItems.js). As permissões que
// não são item de menu ficam junto do menu a que pertencem (Kanban em Fluxos);
// as que não têm item na sidebar ficam no fim.
const getMenusDisponiveis = () => [
  { label: i18n.t("profiles.menus.dashboard"), value: "dashboard" },
  { label: i18n.t("profiles.menus.ticketRatings"), value: "avaliacoes" },
  { label: i18n.t("profiles.menus.tickets"), value: "atendimentos" },
  { label: i18n.t("profiles.menus.ticketsFullHistory"), value: "tickets_historico_completo" },
  { label: i18n.t("profiles.menus.ticketsCall"), value: "tickets_ligar" },
  { label: i18n.t("profiles.menus.pendingMessages"), value: "mensagens_pendentes" },
  { label: i18n.t("profiles.menus.ticketsConference"), value: "tickets_conferencia" },
  { label: i18n.t("profiles.menus.messagesReport"), value: "relatorio_mensagens" },
  { label: i18n.t("profiles.menus.monitoring"), value: "monitoramento" },
  { label: i18n.t("profiles.menus.internalChat"), value: "chat" },
  { label: i18n.t("profiles.menus.flows"), value: "fluxos" },
  { label: i18n.t("profiles.menus.kanbanEditComments"), value: "kanban_editar_comentarios" },
  { label: i18n.t("profiles.menus.kanbanCardInactivityTime"), value: "kanban_config_tempo_inatividade_cards" },
  { label: i18n.t("profiles.menus.quickMessages"), value: "respostas_rapidas" },
  { label: i18n.t("profiles.menus.documentTemplates"), value: "modelos_documentos" },
  { label: i18n.t("profiles.menus.contacts"), value: "contatos" },
  { label: i18n.t("profiles.menus.usageReport"), value: "relatorio_utilizacao" },
  { label: i18n.t("profiles.menus.customers"), value: "clientes" },
  { label: i18n.t("profiles.menus.agenda"), value: "agenda" },
  { label: i18n.t("profiles.menus.schedules"), value: "agendamentos" },
  { label: i18n.t("profiles.menus.tags"), value: "tags" },
  { label: i18n.t("profiles.menus.tagPhrases"), value: "frases_tag" },
  { label: i18n.t("profiles.menus.forbiddenWordsRegister"), value: "termos_proibidos" },
  { label: i18n.t("profiles.menus.forbiddenWordsReport"), value: "termos_proibidos_relatorio" },
  { label: i18n.t("profiles.menus.development"), value: "desenvolvimento" },
  { label: i18n.t("profiles.menus.campaigns"), value: "campanhas" },
  { label: i18n.t("profiles.menus.campaignsList"), value: "campanhas_lista" },
  { label: i18n.t("profiles.menus.campaignsContactLists"), value: "camp_lista_ctt" },
  { label: i18n.t("profiles.menus.campaignsSettings"), value: "camp_configs" },
  { label: i18n.t("profiles.menus.announcements"), value: "infos" },
  { label: i18n.t("profiles.menus.openai"), value: "openai" },
  { label: i18n.t("profiles.menus.integrations"), value: "integracoes" },
  { label: i18n.t("profiles.menus.connectionsView"), value: "conexoes" },
  { label: i18n.t("profiles.menus.connectionsManage"), value: "conexoes_gerenciar" },
  { label: i18n.t("profiles.menus.messageTemplates"), value: "templates_mensagem" },
  { label: i18n.t("profiles.menus.catalog"), value: "catalogo" },
  { label: i18n.t("profiles.menus.files"), value: "lista_de_arquivos" },
  { label: i18n.t("profiles.menus.queues"), value: "filas_e_chatbot" },
  { label: i18n.t("profiles.menus.users"), value: "usuarios" },
  { label: i18n.t("profiles.menus.api"), value: "api" },
  { label: i18n.t("profiles.menus.billing"), value: "financeiro" },
  { label: i18n.t("profiles.menus.profiles"), value: "perfis" },
  { label: i18n.t("profiles.menus.settings"), value: "configs" },
  { label: i18n.t("profiles.menus.tasks"), value: "tarefas" },
  { label: i18n.t("profiles.menus.orders"), value: "pedidos" },
  { label: i18n.t("profiles.menus.help"), value: "ajuda" },
];

const getKanbanSectionLabel = () => i18n.t("profiles.modal.kanbanBoards");

// Busca sem diferenciar maiúsculas nem acentos ("conexao" acha "Conexões").
const normalizeText = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const getFiltrosDisponiveis = () => [
  { label: i18n.t("profiles.filters.showAll"), value: "tickets_filter_show_all" },
  { label: i18n.t("profiles.filters.queues"), value: "tickets_filter_queues" },
  { label: i18n.t("profiles.filters.whatsapps"), value: "tickets_filter_whatsapp" },
  // Autor: GitHub Copilot | Data: 26/03/2026 | Motivo: controlar no perfil se o usuario pode escolher outro responsavel no filtro de follow-up.
  { label: i18n.t("profiles.filters.followupUser"), value: "followup_filter_user" },
];

const parseMenus = (menus) => {
  if (Array.isArray(menus)) return menus;
  if (typeof menus === "string" && menus) {
    try {
      const parsed = JSON.parse(menus);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeKanbanBoardPermissions = (perfil) => {
  if (Array.isArray(perfil?.kanbanBoardPermissions) && perfil.kanbanBoardPermissions.length) {
    return perfil.kanbanBoardPermissions
      .map((item) => ({
        boardId: Number(item.boardId),
        canView: Boolean(item.canView),
        canCreate: Boolean(item.canCreate),
        canEdit: Boolean(item.canEdit),
        canDelete: Boolean(item.canDelete),
        viewOwnCardsOnly: Boolean(item.viewOwnCardsOnly),
      }))
      .filter((item) => Number.isInteger(item.boardId));
  }

  if (Array.isArray(perfil?.kanbanBoardIds)) {
    return perfil.kanbanBoardIds
      .map((boardId) => Number(boardId))
      .filter((boardId) => Number.isInteger(boardId))
      .map((boardId) => ({
        boardId,
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        viewOwnCardsOnly: false,
      }));
  }

  return [];
};

const PerfilModal = ({ open, onClose, perfil }) => {
  const [loading, setLoading] = useState(false);
  const [kanbanBoards, setKanbanBoards] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const formik = useFormik({
    initialValues: {
      perf_name: perfil?.perf_name || "",
      menus: parseMenus(perfil?.menus),
      kanbanBoardPermissions: normalizeKanbanBoardPermissions(perfil),
    },
    enableReinitialize: true,
    validationSchema: PerfilSchema,
    onSubmit: async (values) => {
      const safeValues = {
        ...values,
        menus: parseMenus(values.menus),
        kanbanBoardPermissions: Array.isArray(values.kanbanBoardPermissions)
          ? values.kanbanBoardPermissions
              .map((item) => ({
                boardId: Number(item.boardId),
                canView: Boolean(item.canView),
                canCreate: Boolean(item.canCreate),
                canEdit: Boolean(item.canEdit),
                canDelete: Boolean(item.canDelete),
                viewOwnCardsOnly: Boolean(item.viewOwnCardsOnly),
              }))
              .filter((item) => Number.isInteger(item.boardId))
          : [],
      };
      setLoading(true);
      try {
        if (perfil && perfil.id) {
          await api.put(`/profiles/${perfil.id}`, safeValues);
        } else {
          await api.post("/profiles", safeValues);
        }
        onClose(true);
      } catch (err) {
        // O backend recusa nome de perfil repetido (400). Sem mostrar nada aqui,
        // o admin clicava em Salvar e a tela simplesmente não reagia.
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const loadBoards = async () => {
      if (!open) return;

      try {
        const { data } = await api.get("/kanban/boards", { params: { summary: true } });
        setKanbanBoards(data?.boards || []);
      } catch (err) {
        setKanbanBoards([]);
      }
    };

    loadBoards();
  }, [open]);

  const handleMenuChange = (event) => {
    const { value } = event.target;
    let newMenus = [...formik.values.menus];
    if (newMenus.includes(value)) {
      newMenus = newMenus.filter((m) => m !== value);
    } else {
      newMenus.push(value);
    }
    formik.setFieldValue("menus", newMenus);
  };

  const isAllSelected = (items) =>
    items.every((item) => formik.values.menus.includes(item.value));

  const isSomeSelected = (items) =>
    items.some((item) => formik.values.menus.includes(item.value));

  const handleSelectAll = (items) => {
    const allSelected = isAllSelected(items);
    let newMenus = [...formik.values.menus];

    if (allSelected) {
      newMenus = newMenus.filter(
        (m) => !items.some((item) => item.value === m)
      );
    } else {
      items.forEach((item) => {
        if (!newMenus.includes(item.value)) {
          newMenus.push(item.value);
        }
      });
    }

    formik.setFieldValue("menus", newMenus);
  };

  const handleKanbanBoardsChange = (event) => {
    const values = (Array.isArray(event.target.value) ? event.target.value : [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id));

    const current = Array.isArray(formik.values.kanbanBoardPermissions)
      ? formik.values.kanbanBoardPermissions
      : [];
    const currentOwnCardsOnlyEnabled = current.length > 0 && current.every((item) => Boolean(item.viewOwnCardsOnly));

    const currentMap = new Map(current.map((item) => [Number(item.boardId), item]));
    const nextPermissions = values.map((boardId) => {
      const existing = currentMap.get(boardId);
      if (existing) {
        return existing;
      }

      return {
        boardId,
        canView: true,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        viewOwnCardsOnly: currentOwnCardsOnlyEnabled,
      };
    });

    formik.setFieldValue(
      "kanbanBoardPermissions",
      nextPermissions
    );
  };

  const handleKanbanBoardPermissionChange = (boardId, permissionKey) => (event) => {
    const checked = event.target.checked;
    const current = Array.isArray(formik.values.kanbanBoardPermissions)
      ? formik.values.kanbanBoardPermissions
      : [];

    const updated = current.map((item) => {
      if (Number(item.boardId) !== Number(boardId)) {
        return item;
      }

      const next = {
        ...item,
        [permissionKey]: checked,
      };

      // Sem visualizar, nao deve manter outras permissoes ativas.
      if (permissionKey === "canView" && !checked) {
        next.canCreate = false;
        next.canEdit = false;
        next.canDelete = false;
      }

      if (permissionKey !== "canView" && checked) {
        next.canView = true;
      }

      return next;
    });

    formik.setFieldValue("kanbanBoardPermissions", updated);
  };

  const selectedBoardIds = (formik.values.kanbanBoardPermissions || [])
    .map((item) => Number(item.boardId))
    .filter((id) => Number.isInteger(id));

  const ownCardsOnlyTotal = (formik.values.kanbanBoardPermissions || []).length;
  const ownCardsOnlyEnabledCount = (formik.values.kanbanBoardPermissions || []).filter((item) => Boolean(item.viewOwnCardsOnly)).length;
  const ownCardsOnlyChecked = ownCardsOnlyTotal > 0 && ownCardsOnlyEnabledCount === ownCardsOnlyTotal;
  const ownCardsOnlyIndeterminate = ownCardsOnlyEnabledCount > 0 && ownCardsOnlyEnabledCount < ownCardsOnlyTotal;

  const handleOwnCardsOnlyChange = (event) => {
    const checked = event.target.checked;
    const current = Array.isArray(formik.values.kanbanBoardPermissions)
      ? formik.values.kanbanBoardPermissions
      : [];

    formik.setFieldValue(
      "kanbanBoardPermissions",
      current.map((item) => ({
        ...item,
        viewOwnCardsOnly: checked,
      }))
    );
  };

  const searchTerm = normalizeText(search.trim());
  const matchesSearch = (label) => !searchTerm || normalizeText(label).includes(searchTerm);
  const KANBAN_SECTION_LABEL = getKanbanSectionLabel();
  const visibleMenus = getMenusDisponiveis().filter((item) => matchesSearch(item.label));
  const visibleFilters = getFiltrosDisponiveis().filter((item) => matchesSearch(item.label));
  const showKanbanSection = matchesSearch(KANBAN_SECTION_LABEL);
  const nothingFound = !visibleMenus.length && !visibleFilters.length && !showKanbanSection;

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{perfil ? i18n.t("profiles.modal.editTitle") : i18n.t("profiles.modal.addTitle")}</DialogTitle>
      {/* O form vira coluna flex para só o DialogContent rolar: a busca fica
          fixa em cima e Cancelar/Salvar ficam sempre visíveis embaixo. */}
      <form
        onSubmit={formik.handleSubmit}
        style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}
      >
        <div style={{ padding: "0 24px 8px" }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={i18n.t("profiles.modal.searchPlaceholder")}
            size="small"
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            id="perf_name"
            name="perf_name"
            label={i18n.t("profiles.modal.name")}
            type="text"
            fullWidth
            value={formik.values.perf_name}
            onChange={formik.handleChange}
            error={formik.touched.perf_name && Boolean(formik.errors.perf_name)}
            helperText={formik.touched.perf_name && formik.errors.perf_name}
            variant="outlined"
          />
          {nothingFound && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              {i18n.t("profiles.modal.nothingFound", { term: search.trim() })}
            </Typography>
          )}

          {/* Com busca ativa, "Selecionar todos" vale só para os itens encontrados. */}
          {visibleMenus.length > 0 && (
            <>
              <FormLabel component="legend" style={{ marginTop: 16 }}>
                {i18n.t("profiles.modal.allowedMenus")}
              </FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      indeterminate={
                        isSomeSelected(visibleMenus) &&
                        !isAllSelected(visibleMenus)
                      }
                      checked={isAllSelected(visibleMenus)}
                      onChange={() => handleSelectAll(visibleMenus)}
                      color="primary"
                    />
                  }
                  label={i18n.t("profiles.modal.selectAll")}
                />
                {visibleMenus.map((menu) => (
                  <FormControlLabel
                    key={menu.value}
                    control={
                      <Checkbox
                        checked={formik.values.menus.includes(menu.value)}
                        onChange={handleMenuChange}
                        value={menu.value}
                        color="primary"
                      />
                    }
                    label={menu.label}
                  />
                ))}
              </FormGroup>
            </>
          )}

          {showKanbanSection && (
            <>
              <FormControl fullWidth variant="outlined" style={{ marginTop: 16 }}>
                <InputLabel id="kanban-boards-label">{KANBAN_SECTION_LABEL}</InputLabel>
                <Select
                  labelId="kanban-boards-label"
                  multiple
                  value={selectedBoardIds}
                  onChange={handleKanbanBoardsChange}
                  label={KANBAN_SECTION_LABEL}
                  renderValue={(selected) => {
                    const selectedIds = Array.isArray(selected) ? selected : [];
                    const selectedNames = kanbanBoards
                      .filter((board) => selectedIds.includes(Number(board.id)))
                      .map((board) => board.name);
                    return selectedNames.join(", ");
                  }}
                >
                  {kanbanBoards.map((board) => (
                    <MenuItem key={board.id} value={Number(board.id)}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(formik.values.kanbanBoardPermissions || []).length > 0 && (
                <FormGroup style={{ marginTop: 12 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={ownCardsOnlyChecked}
                        indeterminate={ownCardsOnlyIndeterminate}
                        onChange={handleOwnCardsOnlyChange}
                        color="primary"
                      />
                    }
                    label={i18n.t("profiles.modal.ownCardsOnly")}
                  />
                  {(formik.values.kanbanBoardPermissions || []).map((item) => {
                    const board = kanbanBoards.find((b) => Number(b.id) === Number(item.boardId));
                    return (
                      <div key={item.boardId} style={{ border: "1px solid #ddd", borderRadius: 6, padding: 8, marginBottom: 8 }}>
                        <strong>{board?.name || i18n.t("profiles.modal.boardFallback", { id: item.boardId })}</strong>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(item.canView)}
                                onChange={handleKanbanBoardPermissionChange(item.boardId, "canView")}
                                color="primary"
                              />
                            }
                            label={i18n.t("profiles.modal.permissions.view")}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(item.canCreate)}
                                onChange={handleKanbanBoardPermissionChange(item.boardId, "canCreate")}
                                color="primary"
                              />
                            }
                            label={i18n.t("profiles.modal.permissions.create")}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(item.canEdit)}
                                onChange={handleKanbanBoardPermissionChange(item.boardId, "canEdit")}
                                color="primary"
                              />
                            }
                            label={i18n.t("profiles.modal.permissions.edit")}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(item.canDelete)}
                                onChange={handleKanbanBoardPermissionChange(item.boardId, "canDelete")}
                                color="primary"
                              />
                            }
                            label={i18n.t("profiles.modal.permissions.delete")}
                          />
                        </div>
                      </div>
                    );
                  })}
                </FormGroup>
              )}
            </>
          )}

          {visibleFilters.length > 0 && (
            <>
              <FormLabel component="legend" style={{ marginTop: 16 }}>
                {i18n.t("profiles.modal.ticketFilters")}
              </FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      indeterminate={
                        isSomeSelected(visibleFilters) &&
                        !isAllSelected(visibleFilters)
                      }
                      checked={isAllSelected(visibleFilters)}
                      onChange={() => handleSelectAll(visibleFilters)}
                      color="primary"
                    />
                  }
                  label={i18n.t("profiles.modal.selectAll")}
                />
                {visibleFilters.map((filtro) => (
                  <FormControlLabel
                    key={filtro.value}
                    control={
                      <Checkbox
                        checked={formik.values.menus.includes(filtro.value)}
                        onChange={handleMenuChange}
                        value={filtro.value}
                        color="primary"
                      />
                    }
                    label={filtro.label}
                  />
                ))}
              </FormGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose(false)} color="secondary" variant="outlined" disabled={loading}>
            {i18n.t("common.cancel")}
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={loading}>
            {perfil ? i18n.t("common.save") : i18n.t("profiles.modal.create")}
            {loading && <CircularProgress size={24} style={{ marginLeft: 8 }} />}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PerfilModal;
